import { FC, FocusEvent, KeyboardEvent, useRef, useState } from 'react'
import { IconPaths } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { Button } from 'app/components'
import styles from '../../Prescricao.module.scss'
import { Icone } from './Icone'

type IProps = {
  id: string
  rotulo: string
  valor: string
  onChange: (valor: string) => void
  obrigatorio?: boolean
  /** Mensagem de erro; só aparece (e marca o campo) quando informada. */
  erro?: string
  placeholder?: string
  /** Mais de uma linha vira `textarea`. */
  linhas?: number
  somenteNumeros?: boolean
  dica?: string
  /**
   * Sugestões para completar o texto (ex.: unidades depois de um número). Aparecem enquanto o
   * campo está em uso e a lista não está vazia; ↓ entra nela, Esc fecha, Enter escolhe.
   */
  sugestoes?: string[]
  onSugestao?: (sugestao: string) => void
}

/** Campo de texto com rótulo visível, `*` de obrigatório e erro ligado por `aria-describedby`. */
export const Campo: FC<IProps> = ({
  id,
  rotulo,
  valor,
  onChange,
  obrigatorio,
  erro,
  placeholder,
  linhas = 1,
  somenteNumeros,
  dica,
  sugestoes = [],
  onSugestao,
}) => {
  const { t } = useT()
  const blocoRef = useRef<HTMLDivElement>(null)
  const campoRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
  const [emUso, setEmUso] = useState(false)
  const mostrarSugestoes = emUso && sugestoes.length > 0 && !!onSugestao
  const idSugestoes = `${id}-sugestoes`

  const botoesSugestao = () =>
    Array.from(blocoRef.current?.querySelectorAll<HTMLButtonElement>(`.${styles.sugestao}`) ?? [])

  const teclaNoCampo = (evento: KeyboardEvent<HTMLElement>) => {
    if (!mostrarSugestoes) return
    if (evento.key === 'ArrowDown') {
      evento.preventDefault()
      botoesSugestao()[0]?.focus()
    } else if (evento.key === 'Escape') {
      setEmUso(false)
    }
  }

  const teclaNaSugestao = (evento: KeyboardEvent<HTMLButtonElement>) => {
    const botoes = botoesSugestao()
    const atual = botoes.indexOf(evento.currentTarget)
    if (evento.key === 'ArrowDown') {
      evento.preventDefault()
      botoes[Math.min(atual + 1, botoes.length - 1)]?.focus()
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault()
      if (atual <= 0) campoRef.current?.focus()
      else botoes[atual - 1]?.focus()
    } else if (evento.key === 'Escape') {
      setEmUso(false)
      campoRef.current?.focus()
    }
  }

  const escolher = (sugestao: string) => {
    onSugestao?.(sugestao)
    setEmUso(false)
    campoRef.current?.focus()
  }

  // Fecha a lista só quando o foco sai do campo e das sugestões.
  const aoSair = (evento: FocusEvent<HTMLDivElement>) => {
    if (!blocoRef.current?.contains(evento.relatedTarget as Node | null)) setEmUso(false)
  }
  const idErro = `${id}-erro`
  const idDica = `${id}-dica`
  const descricao = [erro && idErro, dica && idDica].filter(Boolean).join(' ') || undefined
  const comum = {
    id,
    ref: campoRef,
    value: valor,
    onFocus: () => setEmUso(true),
    onKeyDown: teclaNoCampo,
    'aria-controls': mostrarSugestoes ? idSugestoes : undefined,
    placeholder,
    className: styles.campoTexto,
    'aria-required': obrigatorio || undefined,
    'aria-invalid': erro ? true : undefined,
    'aria-describedby': descricao,
  }
  const mudar = (texto: string) => {
    setEmUso(true)
    onChange(somenteNumeros ? texto.replace(/\D/g, '') : texto)
  }

  return (
    <div ref={blocoRef} className='d-flex flex-column gap-1 position-relative' onBlur={aoSair}>
      <label htmlFor={id} className='fs-7 fw-semibold'>
        {rotulo}
        {obrigatorio ? (
          <span aria-hidden className={styles.pendenciaErro}>
            {' '}
            *
          </span>
        ) : (
          <span className={`fw-normal ${styles.textoSecundario}`}>
            {' '}
            {t('prescricao.campo.opcional')}
          </span>
        )}
      </label>
      {linhas > 1 ? (
        <textarea {...comum} rows={linhas} onChange={(evento) => mudar(evento.target.value)} />
      ) : (
        <input
          {...comum}
          inputMode={somenteNumeros ? 'numeric' : undefined}
          onChange={(evento) => mudar(evento.target.value)}
        />
      )}
      {mostrarSugestoes && (
        <ul
          id={idSugestoes}
          className={styles.sugestoes}
          aria-label={t('prescricao.campo.sugestoes')}
        >
          {sugestoes.map((sugestao) => (
            <li key={sugestao}>
              <Button
                unstyled
                className={styles.sugestao}
                onClick={() => escolher(sugestao)}
                onKeyDown={teclaNaSugestao}
              >
                {sugestao}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {dica && (
        <span id={idDica} className={`fs-8 ${styles.textoSecundario}`}>
          {dica}
        </span>
      )}
      {erro && (
        <span id={idErro} className={`${styles.mensagemErro} ${styles.comIcone}`}>
          <Icone icone={IconPaths.Alert} />
          {erro}
        </span>
      )}
    </div>
  )
}
